import { useState } from 'react';
import { toggleId } from '~/utils/toggleId';

export type SelectableContact = { id: string };

export function seedSelectedContactIds(
  eligibleContacts: SelectableContact[],
  initialContactIds: string[]
): Set<string> {
  const eligibleIds = new Set(eligibleContacts.map((contact) => contact.id));
  return new Set(initialContactIds.filter((id) => eligibleIds.has(id)));
}

export function seedFromFirstEligible(
  eligibleContacts: SelectableContact[],
  preferredContactIds: string[],
  fallbackContactIds: string[]
): Set<string> {
  const preferred = seedSelectedContactIds(
    eligibleContacts,
    preferredContactIds
  );
  if (preferred.size > 0) return preferred;
  const fallback = seedSelectedContactIds(eligibleContacts, fallbackContactIds);
  return fallback.size > 0
    ? fallback
    : new Set(eligibleContacts.map((contact) => contact.id));
}

export type ContactDataForDefaults = {
  sellerContacts: SelectableContact[];
  notificationPreference: SelectableContact[];
};

export function defaultContactIdsByOrg(
  contactDataByOrgId: Map<string, ContactDataForDefaults>
): Map<string, string[]> {
  return new Map(
    Array.from(contactDataByOrgId, ([organizationId, contactData]) => {
      return [
        organizationId,
        Array.from(
          seedFromFirstEligible(
            contactData.sellerContacts,
            contactData.notificationPreference.map((user) => user.id),
            []
          )
        ),
      ];
    })
  );
}

export function computeAllSelected(
  contacts: SelectableContact[],
  selectedContactIds: Set<string>
): boolean {
  return (
    contacts.length > 0 &&
    contacts.every((contact) => selectedContactIds.has(contact.id))
  );
}

export function toggleAllContactIds(
  contacts: SelectableContact[],
  allSelected: boolean
): Set<string> {
  return allSelected
    ? new Set()
    : new Set(contacts.map((contact) => contact.id));
}

export function useContactSelection<T extends SelectableContact>(
  contacts: T[],
  initialContactIds: string[]
) {
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(
    () => seedSelectedContactIds(contacts, initialContactIds)
  );

  const allSelected = computeAllSelected(contacts, selectedContactIds);

  const toggleContact = (contactId: string) => {
    setSelectedContactIds((previous) => toggleId(previous, contactId));
  };

  const toggleSelectAll = (currentlyAllSelected: boolean) => {
    setSelectedContactIds(toggleAllContactIds(contacts, currentlyAllSelected));
  };

  return { selectedContactIds, toggleContact, allSelected, toggleSelectAll };
}
